import { useState, useRef, useCallback, useEffect } from 'react';
import { detectWhiteRegion } from '@lib/documentRegionDetector';

/**
 * React hook：定時從 video 元素取樣，呼叫 detectWhiteRegion 分析。
 * 負責 canvas 取樣、定時、cooldown、React state。
 * 核心演算法在 lib/documentRegionDetector.js。
 *
 * @param {HTMLVideoElement} videoEl
 * @param {Object} options
 * @param {number}  options.interval       - 偵測間隔 ms（預設 500）
 * @param {number|string} options.minLightness - 亮度下限（預設 'auto'：直方圖 P80 自適應）
 * @param {number}  options.maxColorRange  - RGB 色差上限（預設 35）
 * @param {number}  options.minAreaRatio   - 最小面積佔比（預設 0.02）
 * @param {boolean} options.enabled        - 是否啟用（預設 true）
 */
export function useWhiteRegionDetector(videoEl, options = {}) {
  const {
    interval = 500,
    minLightness = 'auto',
    maxColorRange = 35,
    minAreaRatio = 0.02,
    enabled = true,
  } = options;

  const [region, setRegion] = useState(null);
  const [debug, setDebug] = useState(null);
  const canvasRef = useRef(null);
  const timerRef = useRef(null);
  const lastDetectedAtRef = useRef(0);

  const SAMPLE_W = 240;
  const SAMPLE_H = 150;
  const DETECTION_COOLDOWN_MS = 1500;

  const detect = useCallback(() => {
    if (!videoEl || !videoEl.videoWidth) return;

    // cooldown
    const cooldownRemaining = DETECTION_COOLDOWN_MS - (Date.now() - lastDetectedAtRef.current);
    if (cooldownRemaining > 0) {
      setDebug(prev => prev ? { ...prev, status: 'cooldown', cooldownMs: cooldownRemaining } : prev);
      return;
    }

    // lazy init canvas
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = SAMPLE_W;
      canvasRef.current.height = SAMPLE_H;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    // 取影片中央 65% 區域，縮小到 SAMPLE_W × SAMPLE_H
    const vw = videoEl.videoWidth;
    const vh = videoEl.videoHeight;
    const margin = 0.175;
    const cropX = vw * margin;
    const cropY = vh * margin;
    const cropW = vw * 0.65;
    const cropH = vh * 0.65;
    ctx.drawImage(videoEl, cropX, cropY, cropW, cropH, 0, 0, SAMPLE_W, SAMPLE_H);
    const imageData = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H);

    // 呼叫純函數演算法
    const result = detectWhiteRegion(imageData.data, SAMPLE_W, SAMPLE_H, {
      minLightness,
      maxColorRange,
      minAreaRatio,
      cropMargin: margin,
    });

    if (result.detected) {
      setDebug({ status: 'locked', detected: true, ...result.debug });
      lastDetectedAtRef.current = Date.now();
      setRegion(result.region);
    } else {
      setDebug({ status: 'detecting', detected: false, ...result.debug });
      setRegion(null);
    }
  }, [videoEl, minLightness, maxColorRange, minAreaRatio]);

  // 定時偵測
  useEffect(() => {
    if (!enabled || !videoEl) {
      setRegion(null);
      return;
    }

    detect();

    timerRef.current = setInterval(detect, interval);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, videoEl, interval, detect]);

  return { region, debug };
}
